import { useLayoutAppKind } from "@ui/layouts/layoutProvider";
import { clipContextForAppKind } from "@ui/molecules/AddToBoard/useAddToBoard";
import { useSearch } from "@utils/usePagination.ts";
import { type Accessor, createEffect, createSignal, type JSX } from "solid-js";
import { createStore, produce } from "solid-js/store";
import { addClipToBoard, updateBoardClip } from "@/client";
import type {
	BoardClipResponse,
	VideoDetailResponse,
} from "@/client/types.gen";
import type { AddBoardFormData } from "~/modules/askgvt/boards/list/components/NewBoardButton";
import {
	AddToBoardContext,
	type BoardInfo,
	buildAddClipBody,
	type ClipAddSource,
	clampClipTrim,
	isClipUnsaved,
	LAST_BOARD_ID_KEY,
	type MoveClipTrim,
	type StoreType,
	type UpdateBoardClipDonePayload,
} from "./addToBoardContext";
import {
	type BoardsListQuery,
	boardsListQueryFor,
	useBoards,
	useBoardsList,
} from "./boardsContext";

declare const pageVars: {
	userId: string;
	board: BoardClipResponse;
	agencyPublicId?: string;
};

function readIsLoading(v: boolean | Accessor<boolean>): boolean {
	return typeof v === "function" ? v() : v;
}

export function AddToBoardProvider(props: {
	projectId?: string;
	board: BoardClipResponse;
	clip?: BoardClipResponse;
	video: VideoDetailResponse;
	clipAddSource?: Accessor<ClipAddSource | null> | ClipAddSource | null;
	onUpdateBoardClip?: (payload: UpdateBoardClipDonePayload) => void;
	children: JSX.Element;
	isLoading: boolean | Accessor<boolean>;
}) {
	const initialBoard = props.board;
	const appKind = useLayoutAppKind();
	const boards = useBoards();

	const [value, setValue] = createStore({
		selectedBoardId: props.board?.id ?? null,
		board: {
			id: initialBoard?.id,
			title: initialBoard?.title,
		},
		clip: props.clip ?? null,
		video: props.video,
		clipAddSource: null as ClipAddSource | null,
		moveClipTrimByItemId: {} as Record<string, MoveClipTrim>,
		isLoading: readIsLoading(props.isLoading),
	});
	const [currentPanel, setCurrentPanel] = createSignal(0);
	const [formData, setFormData] = createStore<AddBoardFormData>({
		title: "",
		description: "",
		privacy: "private",
		icon: "",
	});
	const [errors, setErrors] = createSignal<Record<string, string[]>>({});

	const search = useSearch({});

	const boardsListQuery = (): BoardsListQuery =>
		boardsListQueryFor({
			appKind,
			projectId: props.projectId,
			search: search.search(),
		});

	const boardsList = useBoardsList(() => boardsListQuery());

	createEffect(() => {
		const board = props.board;
		setValue(
			"board",
			produce((b) => {
				b.id = board?.id;
				b.title = board?.title;
			}),
		);
		setValue("clip", props.clip);
		setValue("video", props.video);
	});

	createEffect(() => {
		setValue("isLoading", readIsLoading(props.isLoading));
	});

	createEffect(() => {
		const next =
			typeof props.clipAddSource === "function"
				? props.clipAddSource()
				: (props.clipAddSource ?? null);
		setValue("clipAddSource", next);
	});

	createEffect(() => {
		const clip = value.clip;
		const itemId = clip?.id;
		if (!itemId || !clip) {
			return;
		}
		const src = value.clipAddSource;
		if (src) {
			const vStart = clip.start ?? 0;
			const duration = value.video.duration ?? clip.duration ?? 0;
			setValue(
				"moveClipTrimByItemId",
				produce((rec: Record<string, MoveClipTrim>) => {
					rec[itemId] = clampClipTrim(
						vStart + src.start,
						vStart + src.end,
						duration,
					);
				}),
			);

			return;
		}
		if (!(itemId in value.moveClipTrimByItemId)) {
			const duration = value.video.duration ?? clip.duration ?? 0;
			const trim = clampClipTrim(
				clip.clipped_start ?? clip.start ?? 0,
				clip.clipped_end ?? clip.end ?? undefined,
				duration,
			);
			setValue(
				"moveClipTrimByItemId",
				produce((rec: Record<string, MoveClipTrim>) => {
					rec[itemId] = trim;
				}),
			);
		}
	});

	const storeValue: StoreType = {
		get selectedBoardId() {
			return value.selectedBoardId;
		},
		get board() {
			return value.board;
		},
		get clip() {
			return value.clip;
		},
		get video() {
			return value.video;
		},
		get clipAddSource() {
			return value.clipAddSource;
		},
		get moveClipTrimByItemId() {
			return value.moveClipTrimByItemId;
		},
		get moveClipTrim(): MoveClipTrim {
			const clip = value.clip;
			const itemId = clip?.id;
			if (!itemId || !clip) return { start: 0, end: undefined };
			const saved = value.moveClipTrimByItemId[itemId];
			if (saved !== undefined) return saved;
			return {
				start: clip.clipped_start ?? 0,
				end: clip.clipped_end ?? undefined,
			};
		},
		get isLoading() {
			return value.isLoading;
		},
		get isClipUnsaved() {
			return isClipUnsaved(value.clip, value.board);
		},
		get boardsList() {
			return boardsList;
		},
		search,
		currentPanel,
		formData,
		errors,
	};

	const rememberLastBoardId = (boardId: string) => {
		if (appKind === "studio") {
			return;
		}
		sessionStorage.setItem(LAST_BOARD_ID_KEY, boardId);
	};

	const clipApiContext = () => clipContextForAppKind(appKind);

	return (
		<AddToBoardContext.Provider
			value={[
				storeValue,
				{
					setSelectedBoardId: (boardId: string | null) => {
						setValue("selectedBoardId", boardId);
					},
					onUpdateBoardClip: props.onUpdateBoardClip,
					setBoard: (board: BoardInfo) => {
						setValue(
							"board",
							produce((b) => {
								b.id = board.id;
								b.title = board.title;
							}),
						);
					},
					clearBoard: () => {
						setValue(
							"board",
							produce((b) => {
								b.id = null;
								b.title = null;
							}),
						);
					},
					setClip: (clip: BoardClipResponse) => {
						setValue("clip", clip);
					},
					setMoveClipTrim: (range: MoveClipTrim) => {
						const itemId = value.clip?.id;
						if (!itemId) return;
						setValue(
							"moveClipTrimByItemId",
							produce((rec: Record<string, MoveClipTrim>) => {
								rec[itemId] = range;
							}),
						);
					},
					setFormData,
					setErrors,
					setCurrentPanel,
					rememberLastBoardId,
					saveClipToBoard: async (targetBoardId: string) => {
						const clip = value.clip;
						const board = value.board;
						const moveClipTrim = storeValue.moveClipTrim;
						const videoId = value.video.id;

						if (!videoId) {
							return { ok: false, reason: "missing_video" as const };
						}

						const apiContext = clipApiContext();
						const videoDurationSeconds =
							value.video.duration ?? clip?.duration ?? 0;

						if (isClipUnsaved(clip, board)) {
							const addResult = await addClipToBoard({
								body: buildAddClipBody({
									videoId,
									boardId: targetBoardId,
									moveClipTrim,
									videoDurationSeconds,
									context: apiContext,
									projectId: props.projectId,
									title:
										clip?.title ||
										value.video.post_title ||
										value.video.description ||
										"",
									agencyId: pageVars.agencyPublicId,
								}),
							});
							if (addResult.response.status === 409) {
								return { ok: false, reason: "duplicate" as const };
							}
							if (addResult.response.status !== 200) {
								return { ok: false, reason: "add_failed" as const };
							}
							return {
								ok: true,
								payload: {
									sourceBoardId: undefined,
									targetBoardId,
									clipId: addResult.data?.id,
									moveClipTrim,
								},
							};
						}

						const sourceBoardId = board?.id;
						const clipId = clip?.id;
						if (!sourceBoardId || !clipId) {
							return { ok: false, reason: "update_failed" as const };
						}

						const updateResult = await updateBoardClip({
							path: {
								board_id: sourceBoardId,
								clip_id: clipId,
							},
							body: {
								board_id: targetBoardId,
								clipped_start: moveClipTrim.start,
								clipped_end: moveClipTrim.end,
							},
						});
						if (updateResult.response.status !== 200) {
							return { ok: false, reason: "update_failed" as const };
						}
						return {
							ok: true,
							payload: {
								sourceBoardId,
								targetBoardId,
								clipId,
								moveClipTrim,
							},
						};
					},
					finishClipSave: (
						payload: UpdateBoardClipDonePayload,
						board?: BoardInfo,
					) => {
						const targetId = board?.id ?? payload.targetBoardId ?? undefined;
						if (targetId) {
							rememberLastBoardId(targetId);
						}
						if (board) {
							setValue(
								"board",
								produce((b) => {
									b.id = board.id;
									b.title = board.title;
								}),
							);
							if (board.id) {
								boards.upsertBoardInListCache(
									{ id: board.id, title: board.title ?? undefined },
									boardsListQuery(),
								);
							}
						} else {
							boards.invalidateBoardsList();
						}
						props.onUpdateBoardClip?.(payload);
						boards.invalidateClipMembership();
					},
				},
			]}
		>
			{props.children}
		</AddToBoardContext.Provider>
	);
}
